import { AddedSearchFilters, CommittedSearch, SearchState } from './SearchState';
import { HydrateAction, hydrateType } from '../HydrateAction';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

const initialState: SearchState = {
  lastQueryMetadata: {},
  addedSearchFilters: [],
  committedSearch: null
};

interface SetLastQueryMetadataPayload {
  readonly lastQueryMetadata: Array<{ key: string; value: string | null } | null>;
}

interface SetCommittedSearchPayload {
  readonly committedSearch: CommittedSearch;
}

interface SetAddedSearchFiltersPayload {
  readonly productCode: string;
  readonly filters: string[];
}

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setLastQueryMetadata(state, { payload }: PayloadAction<SetLastQueryMetadataPayload>) {
      const nextMetadata: Record<string, string> = {};
      payload.lastQueryMetadata.forEach((metadata) => {
        if (metadata?.value) {
          nextMetadata[metadata.key] = metadata.value;
        }
      });

      state.lastQueryMetadata = nextMetadata;
    },
    setCommittedSearch(state, { payload }: PayloadAction<SetCommittedSearchPayload>) {
      state.committedSearch = payload.committedSearch;
    },
    addSearchFilters(state, { payload }: PayloadAction<SetAddedSearchFiltersPayload>) {
      const existingProductIndex = state.addedSearchFilters.findIndex(
        (productFilter) => productFilter.productCode === payload.productCode
      );

      if (existingProductIndex !== -1) {
        const existingFilters = state.addedSearchFilters[existingProductIndex].filters;
        const newFilters = payload.filters.filter((filter) => !existingFilters.includes(filter));
        state.addedSearchFilters[existingProductIndex].filters = [
          ...existingFilters,
          ...newFilters
        ];
      } else {
        state.addedSearchFilters.push(payload);
      }
    },
    setEmptySearchFilters(state) {
      state.addedSearchFilters = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateType, (state, action: HydrateAction) => {
      if (action.payload.search) {
        const nextQueryMetadata: Record<string, string> | undefined =
          action.payload.search.lastQueryMetadata;
        if (nextQueryMetadata && Object.keys(nextQueryMetadata).length) {
          state.lastQueryMetadata = nextQueryMetadata;
        }
        const nextAddedProductFilters: Array<AddedSearchFilters> =
          action.payload.search.addedSearchFilters;
        if (nextAddedProductFilters?.length) {
          state.addedSearchFilters = nextAddedProductFilters;
        }
        if (action.payload.search.committedSearch) {
          state.committedSearch = action.payload.search.committedSearch;
        }
      }
    });
  }
});

export const { setEmptySearchFilters, setLastQueryMetadata, setCommittedSearch, addSearchFilters } =
  searchSlice.actions;

export default searchSlice.reducer;
